import { useEffect, useMemo, useRef } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import { useRouter } from 'next/router'
import { Flex } from '@mantine/core'
import { useTranslation } from 'next-i18next'
import { observer } from 'mobx-react-lite'
import { useStore } from '@/store/index'
import useLangPrefix from '@/hooks/useLangPrefix'
import { getTopAbnormalList } from 'api/price'
import type { TopListData } from 'api-price'
import { useDebounceFn, useRequest } from 'ahooks'
import BaseEmpty from '@/components/BaseEmpty'
import { Swiper, SwiperSlide } from 'swiper/react'
import { eventTracker } from '@/utils/collectEvent'
import { useAdapter } from '@/components/Adapter'
import { useRiseColor } from '@/components/BaseRiseColor'
import { GateProIconaV3114PxLineYoujiantou, GateProIconaV3114PxLineZuojiantou } from '@gate/iconfont'
import {
  CoinMoreStyled,
  CoinListStyled,
  CoinListSwiperStyled,
  CoinListSwiperCardStyled,
  CoinListSwiperActionStyled,
  CoinListSwiperButton,
  CoinListSwiperRightButton,
} from '@/blocks/Price/PriceRec/CoinList/style'
import BaseTooltip from '@/components/BaseTooltip'
import MarketListItem from '@/blocks/Price/PriceRec/MarketList/Item'

const MarketList = () => {
  const { locale } = useRouter()
  const { t } = useTranslation()
  const {
    globalStore: { isDark },
  } = useStore()
  const { getRiseColor } = useRiseColor()
  const swiperRef = useRef<SwiperType | null>(null)

  const { priceMarketList, serPriceMarketList } = useStore().baseStore

  const { isMd } = useAdapter()

  const { langPrefix } = useLangPrefix()

  const { run } = useRequest(getTopAbnormalList, {
    defaultParams: [{ page: 1, pageSize: 3, tab: 'all' }],
    onSuccess(data) {
      serPriceMarketList(data as TopListData)
    },
  })

  const leftDisabled = useMemo<boolean>(() => {
    return (priceMarketList?.page ?? 0) <= 1
  }, [priceMarketList?.page])

  const { run: onPageChange } = useDebounceFn(
    (type?: '+' | '-') => {
      let page = priceMarketList?.page ?? 1
      if (type === '+') {
        page++
      }
      if (type === '-') {
        page--
      }
      eventTracker('price_overview_click', {
        tab_name: 'notable_movers',
      })
      run({
        page,
        pageSize: 3,
        tab: 'all',
      })
    },
    { wait: 500 }
  )

  const rightDisabled = useMemo<boolean>(() => {
    return (
      (priceMarketList?.page ?? 0) * 3 >=
      ((priceMarketList?.totalCount ?? 0) >= 20 ? 20 : (priceMarketList?.totalCount ?? 0))
    )
  }, [priceMarketList?.page, priceMarketList?.totalCount])

  const handlePrev = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
      onPageChange('-')
    }
  }

  const handleNext = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
      onPageChange('+')
    }
  }

  return (
    <CoinListStyled isDark={isDark}>
      <Flex
        align="center"
        justify="space-between"
        h={48}
        {...{
          component: 'a',
          href: `${langPrefix}/price/view/notable-change`,
        }}
        onClick={() => {
          eventTracker('price_overview_click', {
            button_name: 'notable_movers_more',
          })
        }}
      >
        <BaseTooltip component="h2" label={t('P.NotableMovers')} className="text-1 font-700 size-20-16">
          {t('P.NotableMovers')}
        </BaseTooltip>

        <CoinMoreStyled align={'center'} gap={2} data-ar={String(locale === 'ar')}>
          <GateProIconaV3114PxLineYoujiantou />
        </CoinMoreStyled>
      </Flex>

      {priceMarketList?.list?.length ? (
        <Flex justify="space-between" direction="column" h={'calc(100% - 48px)'}>
          <CoinListSwiperStyled>
            <Swiper centeredSlides onSwiper={swiper => (swiperRef.current = swiper)} spaceBetween={50}>
              <SwiperSlide>
                <CoinListSwiperCardStyled>
                  {priceMarketList?.list
                    ?.slice(0, 3)
                    .map((item: any) => (
                      <MarketListItem
                        key={`${item?.key}`}
                        {...item}
                        color={[getRiseColor(1).color, getRiseColor(-1).color]}
                      />
                    ))}
                </CoinListSwiperCardStyled>
              </SwiperSlide>
            </Swiper>

            {isMd ? (
              <CoinListSwiperActionStyled className="coin-list-swiper-box">
                <CoinListSwiperButton
                  onClick={handlePrev}
                  type="secondary"
                  disabled={leftDisabled}
                  isHide={leftDisabled}
                  size="xs"
                  leftIcon={<GateProIconaV3114PxLineZuojiantou />}
                  rtlIcon={<GateProIconaV3114PxLineYoujiantou />}
                />
                <CoinListSwiperRightButton
                  isHide={rightDisabled}
                  onClick={handleNext}
                  type="secondary"
                  disabled={rightDisabled}
                  size="xs"
                  leftIcon={<GateProIconaV3114PxLineYoujiantou />}
                  rtlIcon={<GateProIconaV3114PxLineZuojiantou />}
                />
              </CoinListSwiperActionStyled>
            ) : null}
          </CoinListSwiperStyled>
        </Flex>
      ) : (
        <Flex h={150} align="center">
          <BaseEmpty size="xs" />
        </Flex>
      )}
    </CoinListStyled>
  )
}

export default observer(MarketList)
